/**
 * Zones Page
 * Manage delivery zones with polygon drawing
 */

import { useState, useCallback, useEffect } from 'react';
import { Button, Select } from '../components/ui';
import {
  ZoneList,
  ZoneForm,
  ZoneMap,
  ZoneDrivers,
  ZoneStats,
  ZoneRouteDriver,
} from '../components/zones';
import type { ZoneFormData } from '../components/zones';
import {
  useZones,
  useCreateZone,
  useUpdateZone,
  useDeleteZone,
  useDrivers,
} from '../hooks/useApi';
import * as zonesApi from '../services/zones.api';
import { Plus, Loader2 } from 'lucide-react';
import type { Zone, ZoneWithStats } from '../types';
import { useCan } from '../contexts/useAuth';
import { CITIES } from '../utils/cities';

export default function Zones() {
  const can = useCan();
  const [cityId, setCityId] = useState(1);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [drawnCoordinates, setDrawnCoordinates] = useState<number[][][] | null>(null);

  // Queries
  const { data: zones, isLoading: isLoadingZones, refetch: refetchZones } = useZones(cityId, { includeStats: true });

  // Prevent page reload when in edit/drawing mode
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDrawingMode || isFormOpen) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDrawingMode, isFormOpen]);
  const { data: drivers } = useDrivers();

  // Mutations
  const createZoneMutation = useCreateZone();
  const updateZoneMutation = useUpdateZone();
  const deleteZoneMutation = useDeleteZone();

  // Get selected zone
  const selectedZone = zones?.find((z) => z._id === selectedZoneId) as ZoneWithStats | undefined;

  // Handle new zone
  const handleNewZone = useCallback(() => {
    setEditingZone(null);
    setDrawnCoordinates(null);
    setIsFormOpen(true);
    setIsDrawingMode(true);
  }, []);

  // Handle edit zone
  const handleEditZone = useCallback((zoneId: string) => {
    const zone = zones?.find((z) => z._id === zoneId);
    if (zone) {
      setEditingZone(zone as Zone);
      setDrawnCoordinates(null);
      setIsFormOpen(true);
      setIsDrawingMode(true); // Enable drawing mode to allow editing the polygon
    }
  }, [zones]);

  // Handle delete zone (hard delete)
  const handleDeleteZone = useCallback(async (zoneId: string) => {
    if (!confirm('Are you sure you want to permanently delete this zone? This action cannot be undone.')) return;
    
    try {
      await deleteZoneMutation.mutateAsync({ zoneId, hard: true });
      if (selectedZoneId === zoneId) {
        setSelectedZoneId(null);
      }
      refetchZones();
    } catch (error) {
      console.error('Failed to delete zone:', error);
    }
  }, [deleteZoneMutation, selectedZoneId, refetchZones]);

  // Handle polygon drawn
  const handlePolygonDrawn = useCallback((coordinates: number[][][]) => {
    setDrawnCoordinates(coordinates);
    setIsDrawingMode(false);
  }, []);

  // Handle form save
  const handleSaveZone = useCallback(async (data: ZoneFormData) => {
    try {
      if (editingZone) {
        await updateZoneMutation.mutateAsync({
          zoneId: editingZone._id,
          params: {
            name: data.name,
            color: data.color,
            coordinates: data.coordinates,
          },
        });
      } else {
        await createZoneMutation.mutateAsync({
          name: data.name,
          cityId,
          coordinates: data.coordinates,
          color: data.color,
        });
      }
      
      setIsFormOpen(false);
      setEditingZone(null);
      setDrawnCoordinates(null);
      refetchZones();
    } catch (error) {
      console.error('Failed to save zone:', error);
    }
  }, [editingZone, cityId, createZoneMutation, updateZoneMutation, refetchZones]);

  // Handle form cancel
  const handleCancelForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingZone(null);
    setDrawnCoordinates(null);
    setIsDrawingMode(false);
  }, []);

  // Handle add/remove driver
  const handleAddDriver = useCallback(async (driverUid: string) => {
    if (!selectedZoneId) return;
    try {
      await zonesApi.addDriverToZone(selectedZoneId, driverUid);
      refetchZones();
    } catch (error) {
      console.error('Failed to add driver to zone:', error);
    }
  }, [selectedZoneId, refetchZones]);

  // Keyed by zone so selecting another zone does not show this one's error.
  const [routeDriverError, setRouteDriverError] = useState<{ zoneId: string; message: string } | null>(null);

  const handleRouteDriverChange = useCallback(async (driverUid: string | null) => {
    if (!selectedZoneId) return;
    setRouteDriverError(null);
    try {
      await updateZoneMutation.mutateAsync({ zoneId: selectedZoneId, params: { routeDriverUid: driverUid } });
      await refetchZones();
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } } };
      setRouteDriverError({ zoneId: selectedZoneId, message: err?.response?.data?.error || 'Failed to save the route driver' });
    }
  }, [selectedZoneId, updateZoneMutation, refetchZones]);

  const handleRemoveDriver = useCallback(async (driverUid: string) => {
    if (!selectedZoneId) return;
    try {
      await zonesApi.removeDriverFromZone(selectedZoneId, driverUid);
      refetchZones();
    } catch (error) {
      console.error('Failed to remove driver from zone:', error);
    }
  }, [selectedZoneId, refetchZones]);

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Zones</h1>
          <p className="text-gray-500 text-sm">Manage delivery zones and driver assignments</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-40">
            <Select
              options={CITIES}
              value={cityId}
              onChange={(e) => setCityId(parseInt(e.target.value, 10))}
            />
          </div>
          {can('zones.manage') && (
            <Button
              variant="primary"
              onClick={handleNewZone}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              New Zone
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Zone List */}
        <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
          <div className="p-4 overflow-y-auto flex-1">
            {isLoadingZones ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <ZoneList
                zones={(zones as ZoneWithStats[]) || []}
                selectedZoneId={selectedZoneId || undefined}
                onSelectZone={setSelectedZoneId}
                onEditZone={handleEditZone}
                onDeleteZone={handleDeleteZone}
              />
            )}
          </div>
        </div>

        {/* Middle - Map */}
        <div className="flex-1 relative">
          <ZoneMap
            zones={(zones as Zone[]) || []}
            selectedZoneId={selectedZoneId || undefined}
            onZoneSelect={setSelectedZoneId}
            onPolygonDrawn={handlePolygonDrawn}
            isDrawingMode={isDrawingMode}
            editingZone={editingZone}
            className="h-full w-full"
          />
        </div>

        {/* Right Panel - Zone Details / Form */}
        <div className="w-96 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
          <div className="p-4 overflow-y-auto flex-1 space-y-4">
            {isFormOpen ? (
              <ZoneForm
                zone={editingZone}
                coordinates={drawnCoordinates || undefined}
                onSave={handleSaveZone}
                onCancel={handleCancelForm}
                isSaving={createZoneMutation.isPending || updateZoneMutation.isPending}
                isDrawingMode={isDrawingMode}
              />
            ) : selectedZone ? (
              <div className="flex flex-col h-full">
                {/* Zone Header */}
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{selectedZone.name}</h2>
                  <div 
                    className="w-full h-2 rounded mt-2" 
                    style={{ backgroundColor: selectedZone.color }}
                  />
                </div>

                {/* Zone Stats - Compact */}
                <ZoneStats stats={selectedZone.stats || null} />

                <div className="mt-4">
                  <ZoneRouteDriver
                    key={selectedZone._id}
                    value={selectedZone.route_driver_uid}
                    drivers={drivers?.map((d) => ({ driver_uid: d.driver_uid, full_name: d.full_name })) || []}
                    canEdit={can('zones.manage')}
                    saving={updateZoneMutation.isPending}
                    error={routeDriverError?.zoneId === selectedZone._id ? routeDriverError.message : null}
                    onChange={handleRouteDriverChange}
                  />
                </div>

                {/* Zone Drivers - Takes remaining space */}
                <div className="flex-1 mt-4 min-h-0">
                  <ZoneDrivers
                    zoneId={selectedZone._id}
                    zoneName={selectedZone.name}
                    assignedDriverUids={selectedZone.default_driver_uids}
                    availableDrivers={drivers?.map((d) => ({
                      driver_uid: d.driver_uid,
                      full_name: d.full_name,
                    })) || []}
                    onAddDriver={handleAddDriver}
                    onRemoveDriver={handleRemoveDriver}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Select a zone to view details</p>
                <p className="text-sm mt-1">or create a new zone</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
